import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { getDefaultCustomer } from "../Store";
import { useAuth } from "./auth-context";
import { Customer, Product, Schein } from "./interfaces";

type ScheinContextType = {
  day: Date;
  startTime: Date;
  endTime: Date;
  setDay: (newDay: Date) => void;
  setStartTime: (newStartTime: Date) => void;
  setEndTime: (newEndTime: Date) => void;
  fahrtBerechnen: boolean;
  setFahrtBerechnen: (val: boolean) => void;
  products: Product[];
  setProducts: (products: Product[]) => void;
  selectedProducts: Product[];
  setSelectedProducts: (products: Product[]) => void;
  searchedProducts: Product[];
  searchProduct: (search: string) => void;
  comment: string;
  setComment: (text: string) => void;
  selectedCustomer: Customer;
  setSelectedCustomer: (kunde: Customer) => void;
  customerSignature: string;
  setCustomerSignature: (custSignature: string) => void;
  technicianSignature: string;
  setTechnicianSignature: (custSignature: string) => void;
  startOrt: string;
  setStartOrt: (val: string) => void;
  zielOrt: string;
  setZielOrt: (val: string) => void;
  distanz: number;
  setDistanz: (val: number) => void;
  fahrtZeit: number;
  setFahrtZeit: (val: number) => void;
  images: string[];
  setImages: (images: string[]) => void;
  currentSchein: Schein | undefined;
  setCurrentSchein: (s: Schein) => void;
  persistSchein: () => void;
  pdf: string;
  setPdf: (val: string) => void;
};

const ScheinContext = createContext<ScheinContextType | undefined>(undefined);

type ScheinProviderProps = {
  children: ReactNode;
};

export const useScheinContext = () => {
  const context = useContext(ScheinContext);
  if (context === undefined) {
    throw new Error("useScheinContext must be used within a ScheinProvider");
  }
  return context;
};

export const ScheinProvider = ({ children }: ScheinProviderProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [searchedProducts, setSearchedProducts] = useState<Product[]>([]);
  const [day, setDay] = useState(new Date());

  const [endTime, setEndTime] = useState(new Date());
  const [selectedCustomer, setSelectedCustomer] = useState(getDefaultCustomer);
  const [comment, setComment] = useState("");
  const [fahrtBerechnen, setFahrtBerechnen] = useState(false);

  const [startOrt, setStartOrt] = useState("");
  const [zielOrt, setZielOrt] = useState("");
  const [distanz, setDistanz] = useState(0);
  const [fahrtZeit, setFahrtZeit] = useState(0); // hier in min rechnen

  const currentDate = new Date();
  const twoHoursAgo = new Date(currentDate.getTime() - 2 * 60 * 60 * 1000);
  const [startTime, setStartTime] = useState(twoHoursAgo);

  const [customerSignature, setCustomerSignature] = useState("");
  const [technicianSignature, setTechnicianSignature] = useState("");

  const [images, setImages] = useState<string[]>([]);

  const [currentSchein, setCurrentSchein] = useState<Schein | undefined>();

  const [pdf, setPdf] = useState("");

  const [snr, setSnr] = useState<string>("");

  const { name } = useAuth();

  const persistSchein = () => {
    let currentSnr = snr;

    if (currentSnr.length === 0) {
      currentSnr = generateCustomId();
      setSnr(currentSnr);
    }

    const schein: Schein = {
      fahrtberechnen: fahrtBerechnen,
      pos: selectedProducts,
      datum: day,
      kommentar: comment,
      kdnr: selectedCustomer.Kundennummer.toString(),
      startort: startOrt,
      zielort: zielOrt,
      bearbeiter: name,
      snr: currentSnr,
      startTime: startTime,
      endTime: endTime,
      pdf: "",
    };
    // console.log("persisting schein_" + snr);
    localStorage.setItem("schein_" + currentSnr, JSON.stringify(schein));
    localStorage.setItem(
      "kunde_" + currentSnr,
      JSON.stringify(selectedCustomer)
    );
  };

  function generateCustomId(length = 10) {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let customId = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      customId += characters[randomIndex];
    }
    return customId;
  }

  const searchProduct = (search: string) => {
    // Implementiere Logik, um Produkte basierend auf den Suchkriterien zu filtern
    if (search === "") {
      setSearchedProducts([]);
    } else {
      const filteredProducts = products.filter((product) =>
        product.artikel.toLowerCase().includes(search.toLowerCase())
      );
      setSearchedProducts(filteredProducts);
    }
  };

  useEffect(() => {
    if (searchedProducts === undefined) {
      console.trace("searchedProducts is undefined");
    }
  }, [searchedProducts]);

  const contextValue: ScheinContextType = {
    products,
    setProducts,
    selectedProducts,
    setSelectedProducts,
    searchedProducts,
    day,
    startTime,
    endTime,
    fahrtBerechnen,
    setFahrtBerechnen,
    searchProduct,
    setDay,
    setStartTime,
    setEndTime,
    comment,
    setComment,
    startOrt,
    setStartOrt,
    zielOrt,
    setZielOrt,
    distanz,
    setDistanz,
    fahrtZeit,
    setFahrtZeit,
    selectedCustomer,
    setSelectedCustomer,
    customerSignature,
    setCustomerSignature,
    technicianSignature,
    setTechnicianSignature,
    images,
    setImages,
    currentSchein,
    setCurrentSchein,
    persistSchein,
    pdf,
    setPdf,
  };

  return (
    <ScheinContext.Provider value={contextValue}>
      {children}
    </ScheinContext.Provider>
  );
};
